import {
  LocationDto,
  ProductInfoDto,
  ProductLine,
  ScaleTraxTicketDto,
  TicketDto,
  TicketResponseDto,
  UrlKeyDto,
} from '@trucktrax/trucktrax-ts-common';
import { DisposalMethodDto } from '@trucktrax/trucktrax-ts-common/build/Dtos/Tickets/DisposalMethodDto';
import { VoidReasonDto } from '@trucktrax/trucktrax-ts-common/build/Dtos/Tickets/VoidReasonDto';
import { Dispatch } from 'redux';

import { MarkerType } from '../components/geotrax/map/TTMap';
import { OPEN_POPUP, SELECT_MARKER } from '../constants/actionConstants';
import {
  QUALITY_FACTORS_PATH,
  TERTIARYDATA_PATH,
  TICKETS_PATH,
  TICKETS_WITH_CURRENT_STATUS_PATH,
} from '../constants/apiConstants';
import { INVALID_STALE_TOKEN_LOGOUT } from '../constants/appConstants';
import {
  ERROR_TEXT_FETCH_CURRENT_TICKET_LIST,
  ERROR_TEXT_FETCH_ORDER_TICKET_LIST,
  ERROR_TEXT_FETCH_TICKET_QUALITY_FACTORS,
} from '../constants/errorConstants';
import HTTP_CODES from '../constants/httpConstants';
import { store } from '../Main';
import { selectMarker } from '../store/actions/mapviewActions';
import {
  saveTicketListFilters,
  selectTicket,
  setCurrentTicketQualityFactors,
  setTicketList,
  syncIncomingTicket,
} from '../store/actions/ticketActions';
import { ReduxState } from '../store';
import { selectIncomingTicketById } from '../store/selectors/incomingMaterialsSelectors';
import { MaterialReceiptFields, TicketListFilters, TicketWithCurrentStatusDto } from '../types';
import { getTicketingBaseUrl } from '../util/apiUtil';
import { getTokens } from '../util/authUtil';
import DateTimeRange from '../util/DateTimeRange';
import recordLog, { devErrorAndLog } from '../util/errorUtil';
import { updatePositionWithTruckEta } from './mapviewsService';
import { getRequest, postRequest, putRequest } from './requestService';
import TicketingSocket from './sockets/ticketingSocket';
import { fetchStatusList } from './statusesService';
import { getIdFromUrl } from '../util/appUtil';
import { MarkerDataType } from '../components/geotrax/map/RouteHistoryTTMap';
import { getLatestWorkflowStatus, statusAllowsETACalculation } from '../util/statusesUtil';
import { determineMarkerPosition, determineMarkerPositionId } from '../util/markerUtil';

export const tertiaryDataChange$ = TicketingSocket.tertiaryDataChange$.asObservable();

export async function getTicketsByOrderId(orderId: number | string) {
  const url = getTicketingBaseUrl() + TICKETS_PATH;
  const params = {
    orderId,
    deleted: false,
  };
  const response = await getRequest(url, params);
  return response.data.items;
}

export function fetchOrderTicketList(orderId: number | string) {
  return async (dispatch: Dispatch): Promise<any[]> => {
    try {
      return await getTicketsByOrderId(orderId);
    } catch (e: any) {
      const consoleOnly = e.response?.status === HTTP_CODES.forbidden;
      dispatch(
        devErrorAndLog(
          e.toString(),
          ERROR_TEXT_FETCH_ORDER_TICKET_LIST,
          e.toString(),
          undefined,
          consoleOnly,
        ) as any,
      );
      return [];
    }
  };
}

export const fetchPreviousTicket = async (
  plantUrl: string,
  truckUrl?: string,
): Promise<TicketDto | undefined> => {
  const url = getTicketingBaseUrl() + TICKETS_PATH;
  const params = {
    plantUrl,
    sortBy: 'ticketedTimestamp,DESC',
    size: 1,
  };

  const withTruckParams = truckUrl ? { ...params, truckUrl } : params;
  const response = await getRequest(url, withTruckParams);
  return response.data.items[0];
};

export async function updateTicket(ticket: TicketDto) {
  const url = `${getTicketingBaseUrl()}${TICKETS_PATH}/${ticket.id}`;
  try {
    const response = await putRequest(url, ticket);
    return response.data as TicketDto;
  } catch (e: any) {
    recordLog('error', `failed to update ticket - dto: ${JSON.stringify(ticket)} `, e.toString());
    return e;
  }
}

export async function closeOpenTicket(ticket: TicketDto) {
  const url = `${getTicketingBaseUrl()}${TICKETS_PATH}/close-open-ticket/${ticket.id}`;
  try {
    const response = await putRequest(url, ticket);
    return response.data as TicketDto;
  } catch (e: any) {
    recordLog('error', `failed to update ticket - dto: ${JSON.stringify(ticket)} `, e.toString());
    return e;
  }
}

/**
 * Update the delivery location (geozone / address) of an existing ticket.
 * Backend: PUT /ticketing/api/tickets/update-location/{ticketId}
 * The backend merges only the delivery-location fields (geoZone, address,
 * deliveryInstructions, plant/region) and re-derives the destination plant,
 * then re-statuses the ticket against the new geozone. It rethrows on failure,
 * so callers should handle the rejected promise to surface a message.
 */
export async function updateDeliveryLocation(
  ticket: TicketDto,
  diversionReason?: string,
  diversionReasonNotes?: string,
) {
  const url = `${getTicketingBaseUrl()}${TICKETS_PATH}/update-location/${ticket.id}`;
  // The optional reason and notes ride as query parameters beside the request — never on
  // the ticket payload — and land on the diversion record. The reason is the backend enum
  // member name (see DIVERSION_REASON_OPTIONS); an unknown value is rejected with a 400.
  const params = {
    ...(diversionReason ? { diversionReason } : {}),
    ...(diversionReasonNotes ? { diversionReasonNotes } : {}),
  };
  const response = await putRequest(url, ticket, params);
  return response.data as TicketDto;
}

/**
 * DEV-4153: acknowledge receipt of an Incoming Materials ticket.
 * Backend: POST /ticketing/api/tickets/{id}/material-received — the ticketing
 * BFF's TicketingController, which forwards to the tickets microservice's
 * acknowledge-material-received action and then broadcasts the updated ticket
 * over the ticketing socket. The BE stamps the current user and UTC time
 * (idempotent; rejects with 400 if the ticket has never reached OnJob).
 * Rethrows on failure so callers can surface an error toast.
 */
export async function acknowledgeMaterialReceived(ticketId: number) {
  const url = `${getTicketingBaseUrl()}${TICKETS_PATH}/${ticketId}/material-received`;
  const response = await postRequest(url);
  return response.data as TicketDto & MaterialReceiptFields;
}

/**
 * DEV-4153: thunk — acknowledge, then merge the BE stamp into whatever rows
 * the store holds NOW, not the row the caller rendered. The confirm modal can
 * sit open while position frames advance `currentStatus` or the ticket is
 * removed from the slice; merging into a render-time snapshot would revert
 * the live status or resurrect a removed ticket via SYNC_INCOMING_TICKET's
 * whole-row replace. If the ticket has left the slice, skip the dispatch —
 * the acknowledgement is recorded on the BE either way. The drill-down
 * renders from the separate selectedTicket slice, so stamp that too when it
 * still shows this ticket — whichever surface the user acknowledged from
 * (list checkbox or drill-down button), an open drill-down swaps its Mark as
 * Received button for the acknowledgement block immediately. Same refresh
 * idiom as UpdateDeliveryLocationModal after divert: the SyncTicket socket
 * path updates the lists and retains the active current IM selection; this
 * explicit stamp also refreshes matching drill-downs in other contexts.
 * Rethrows on failure.
 */
export function acknowledgeIncomingTicket(ticketId: number) {
  return async (dispatch: Dispatch, getState: () => ReduxState) => {
    const updated = await acknowledgeMaterialReceived(ticketId);
    const heldTicket = selectIncomingTicketById(getState(), ticketId);

    if (heldTicket) {
      dispatch(
        syncIncomingTicket({
          ...heldTicket,
          materialReceivedBy: updated.materialReceivedBy,
          materialReceivedAt: updated.materialReceivedAt,
        }),
      );
    }

    const selected = getState().selectedTicket as (TicketDto & MaterialReceiptFields) | null;
    if (selected?.id === ticketId) {
      const stamped: TicketDto & MaterialReceiptFields = {
        ...selected,
        materialReceivedBy: updated.materialReceivedBy,
        materialReceivedAt: updated.materialReceivedAt,
      };
      dispatch(selectTicket(stamped));
    }
    return updated;
  };
}

export const fetchTicket = async (id: number): Promise<TicketDto | undefined> => {
  const url = `${getTicketingBaseUrl() + TICKETS_PATH}/${id}`;
  const response = await getRequest(url);
  return response.data;
};

export const fetchDisposalMethods = async (regionUrl: string): Promise<DisposalMethodDto[]> => {
  const url = `${getTicketingBaseUrl()}/disposalmethods`;
  const params = { regionUrl };
  const response = await getRequest(url, params);
  return response.data;
};

export const fetchVoidReasons = async (regionUrl: string): Promise<VoidReasonDto[]> => {
  const url = `${getTicketingBaseUrl()}/voidreasons`;
  const params = { regionUrl };
  const response = await getRequest(url, params);
  return response.data;
};

export function fetchCurrentTicketQualityFactors(ticketId: number) {
  return async (dispatch: Dispatch) => {
    const url = `${getTicketingBaseUrl()}${TICKETS_PATH}/${ticketId}${QUALITY_FACTORS_PATH}`;
    try {
      const response = await getRequest(url);
      dispatch(setCurrentTicketQualityFactors(response.data));
    } catch (e: any) {
      const consoleOnly = e.response?.status === HTTP_CODES.forbidden;
      dispatch(
        devErrorAndLog(
          ERROR_TEXT_FETCH_TICKET_QUALITY_FACTORS,
          `url:${url}`,
          e.toString(),
          undefined,
          consoleOnly,
        ) as any,
      );
    }
  };
}

/**
 * Fetch the current incoming tickets for the user's destination plants, with
 * each ticket's `currentStatus` populated. We use `/with-current-status` (not
 * the plain `/tickets` endpoint) because the IM page needs the real workflow
 * step the truck has reached — `ticket.externalInfo.status` from the plain
 * endpoint is populated from a different source and frequently lags or reports
 * a status that is not in the IM workflow (e.g. `DeliveryStart`).
 *
 * This is the lighter of the two status-attaching endpoints; it returns
 * `currentStatus` only, without the full `statusHistory` array.
 */
export async function fetchIncomingTicketsWithCurrentStatus(
  destinationPlants: string[],
): Promise<TicketWithCurrentStatusDto[]> {
  if (destinationPlants.length === 0) return [];
  const url = getTicketingBaseUrl() + TICKETS_WITH_CURRENT_STATUS_PATH;
  const params = {
    destinationPlants,
    currentTicketOnly: true,
    deleted: false,
    isArchived: false,
  };
  // Errors propagate to the caller, which keeps the previous ticket list on
  // transient failure rather than wiping the IM sidebar.
  const response = await getRequest(url, params);
  const tickets: TicketWithCurrentStatusDto[] = response.data.items ?? [];
  return tickets.filter(t => !!t.destinationPlant);
}

export async function fetchCurrentTicketForTruck(
  truckUrl: string,
  region?: string,
  selectedProductLine?: string,
) {
  const url = getTicketingBaseUrl() + TICKETS_PATH;
  const params: any = {
    truckUrl,
    regionUrl: region,
    plantProductLine: selectedProductLine,
    currentTicketOnly: true,
    deleted: false,
    isArchived: false,
    size: 1,
  };

  try {
    const response = await getRequest(url, params);
    const ticket = response.data.items[0];
    return ticket;
  } catch (e: any) {
    if (e !== INVALID_STALE_TOKEN_LOGOUT) {
      const consoleOnly = e.response?.status === HTTP_CODES.forbidden;
      devErrorAndLog(
        e.toString(),
        ERROR_TEXT_FETCH_CURRENT_TICKET_LIST,
        e.toString(),
        undefined,
        consoleOnly,
      );
    }
    return null;
  }
}

export function fetchCurrentTicketList(
  region?: string,
  selectedProductLine?: string,
  plantUrl?: string,
  plantsFilter?: string[],
) {
  return async (dispatch: Dispatch) => {
    const url = getTicketingBaseUrl() + TICKETS_PATH;
    const params: any = {
      regionUrl: region,
      isArchived: false,
      plantProductLine: selectedProductLine,
      currentTicketOnly: true,
      deleted: false,
    };

    const withPlantParams = plantUrl ? { ...params, plant: plantUrl } : params;
    if (plantsFilter) {
      withPlantParams.plants = plantsFilter;
    }
    try {
      const response = await getRequest(url, withPlantParams);
      dispatch(setTicketList(response.data.items));
    } catch (e: any) {
      if (e !== INVALID_STALE_TOKEN_LOGOUT) {
        const consoleOnly = e.response?.status === HTTP_CODES.forbidden;
        dispatch(
          devErrorAndLog(
            e.toString(),
            ERROR_TEXT_FETCH_CURRENT_TICKET_LIST,
            e.toString(),
            undefined,
            consoleOnly,
          ) as any,
        );
      }
    }
  };
}

export async function fetchTicketList(
  regionUrl?: string,
  inProductLines?: string,
  timespan?: DateTimeRange,
) {
  const url = getTicketingBaseUrl() + TICKETS_PATH;
  const dateTimestampBefore = new Date(timespan?.start || '');
  const dateTimestampAfter = new Date(timespan?.end || '');
  const params = {
    regionUrl,
    inProductLines,
    timestampBefore: dateTimestampAfter.toUTCString(),
    timestampAfter: dateTimestampBefore.toUTCString(),
    deleted: false,
  };
  try {
    const response = await getRequest(url, params);
    return response.data.items;
  } catch (e) {
    if (e !== INVALID_STALE_TOKEN_LOGOUT) {
      throw e;
    }
    return undefined;
  }
}

export async function getTertiaryData(ticketId: number): Promise<TertiaryDataDto | null> {
  try {
    const url = `${getTicketingBaseUrl()}${TICKETS_PATH}/${ticketId}${TERTIARYDATA_PATH}`;
    const response = await getRequest(url);
    return response.data as TertiaryDataDto;
  } catch (e: any) {
    const consoleOnly = e?.response?.status === HTTP_CODES.forbidden;
    devErrorAndLog(
      e.toString(),
      ERROR_TEXT_FETCH_CURRENT_TICKET_LIST,
      e.toString(),
      undefined,
      consoleOnly,
    );
    return null;
  }
}

export async function createTicket(ticket: ScaleTraxTicketDto) {
  const url = `${getTicketingBaseUrl()}${TICKETS_PATH}/createclearviewticket`;
  const { currentRegion } = store.getState();
  const tenantUrl = getTokens().tenantUrl!;
  const regionUrl = currentRegion?.url;
  const params = { tenantUrl, regionUrl };
  let response;
  try {
    response = await postRequest(url, ticket, params);
    return response.data as TicketResponseDto;
  } catch (e: any) {
    recordLog(
      'error',
      `ScaleTraxTicketing failed to createTicket - dto: ${JSON.stringify(ticket)} `,
      `${e.toString()}Error Message: ${e.response.data.message}`,
    );
    return e.response;
  }
}

export async function ticketSocketDisconnect() {
  await TicketingSocket.disconnect(true);
}

export async function ticketSocketInit(currentRegion: UrlKeyDto, selectedProductLine: ProductLine) {
  // Avoiding a race condition in completion of websocket disconnect
  await ticketSocketDisconnect();
  TicketingSocket.connect(currentRegion, selectedProductLine);
}

export async function changeTicketSocketConnection(
  currentRegion: UrlKeyDto,
  selectedProductLine: ProductLine,
): Promise<void> {
  await TicketingSocket.attemptConnectionChange(currentRegion, selectedProductLine);
}

type GetState = () => ReduxState;

export const selectRouteMarker =
  (marker: MarkerType) => async (dispatch: Dispatch<any>, getState: GetState) => {
    const { currentRegion, selectedProductLine } = getState();
    dispatch({
      type: SELECT_MARKER,
      payload: { ...marker, routeHistoryMarker: true },
    });
    const regionId = getIdFromUrl(currentRegion?.url);
    const markerProductLine =
      (marker.markerData as MarkerDataType)?.productLine || selectedProductLine;
    dispatch({
      type: OPEN_POPUP,
      payload: {
        markerId: marker.markerId,
        regionId,
        productLine: markerProductLine,
        routeHistoryMarker: true,
      },
    });
  };

export const selectTruckMarker =
  (marker: MarkerType, ticketUrl: string) =>
  async (dispatch: Dispatch<any>, getState: GetState) => {
    // immediate dispatch is used for UI responsiveness
    // to avoid hanging while determining latest status
    dispatch(selectMarker(marker as any));

    const { positionList, incomingPositionList, incomingTicketsList, currentRegion, plantList } =
      getState();
    const markerPositionId = determineMarkerPositionId(marker);
    // Incoming Materials trucks live in incomingPositionList, not positionList.
    // Fall back to the marker's embedded positionDetails if neither slice has it.
    const position =
      positionList.find(pos => markerPositionId === pos.id) ??
      incomingPositionList?.find(pos => markerPositionId === pos.id) ??
      determineMarkerPosition(marker);

    const plantUrl = position?.ticket?.plant?.url;
    let plantLocation: LocationDto | undefined;
    if (plantUrl) {
      plantLocation = plantList?.find(plant => plant.url === plantUrl)?.location;
    }

    if (!currentRegion?.url || !ticketUrl) {
      return;
    }

    // For Incoming Materials, the ticket can belong to a different region than
    // the user's currentRegion (the truck is en route from another region).
    // Use the ticket's own region for the status fetch — otherwise the BE
    // filters by the wrong region and returns zero status events.
    const incomingTicket = incomingTicketsList?.find(t => t.url === ticketUrl);
    const ticketRegionUrl =
      incomingTicket?.region?.url ?? position?.ticket?.region?.url ?? currentRegion?.url;
    const ticketInfo: Pick<TicketDto, 'region' | 'url'> = {
      region: { url: ticketRegionUrl },
      url: ticketUrl,
    };

    const isExternal = position?.driver?.isExternal ?? false;
    const statuses = await fetchStatusList(ticketInfo)(dispatch);
    const ticketStatus = getLatestWorkflowStatus(statuses, isExternal, position?.productLine);

    const includeETA = statusAllowsETACalculation(ticketStatus?.status);
    if (!includeETA) {
      return;
    }

    const positionWithStatus = {
      ...position,
      ticketStatus,
      plantLocation,
    } as any;
    dispatch(updatePositionWithTruckEta(positionWithStatus));
  };

export const selectOrderMarker = (marker: MarkerType) => (dispatch: Dispatch) => {
  dispatch(selectMarker(marker as any) as any);
};

export const saveFilters = (filters: TicketListFilters) => (dispatch: Dispatch) => {
  dispatch(saveTicketListFilters(filters));
};

export interface TertiaryDataDto {
  loadsCount?: number;
  extraProducts: ProductInfoDto[];
}
